import { Modal, ModalBody, ModalTitle, ModalFooter, MODAL_INNER_GAP } from 'design-system/src/components/Modal';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { PharmaPilotDrug } from '../../types';
import Button from 'design-system/src/components/Button/Button';
import Hairline from 'design-system/src/components/Hairline/Hairline';
import styled from 'styled-components';
import { UseMutationResult } from '@tanstack/react-query';
import { Drug, NewCommentFile, Patient } from '../../api';
import { Task } from './types';
import { ExtraInfoTable } from 'design-system/src/components/ExtraInfoTable/ExtraInfoTable';
import { Checkbox } from 'design-system/src/components/Checkbox/Checkbox';
import CommentAndFileSection from './CommentAndFileSection';
import { useId } from 'react';

const StyledCommentAndFileSection = styled(CommentAndFileSection)`
  padding-bottom: 0;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  background-color: var(--grayscale-50);
  border-radius: var(--border-radius-small);
`;

const CheckboxLabel = styled(Typography)`
  cursor: pointer;
`;

export type AdditionalInput = {
  needs_draft_review?: boolean;
};

type AppealRequestModal = {
  closeModal: () => void;
  initiate: UseMutationResult<void, Error, void>;
  inputRef: React.RefObject<HTMLTextAreaElement>;
  patient: Patient;
  drug: Drug;
  task: Task;
  files: NewCommentFile[];
  onFilesChange: (files: NewCommentFile[]) => void;
  setAdditionalInput: (additionalInput: AdditionalInput) => void;
  additionalInput: AdditionalInput;
  inputValue: string;
  setInputValue: (value: string) => void;
  appealRequestModalType: AppealRequestModalType;
};

export type AppealRequestModalType = 'initiate_with_next_steps' | 'revise_with_next_steps' | 'revise_only';

const AppealRequestModal = ({
  closeModal,
  patient,
  drug,
  task,
  initiate,
  inputRef,
  files,
  onFilesChange,
  setAdditionalInput,
  additionalInput,
  inputValue,
  setInputValue,
  appealRequestModalType,
}: AppealRequestModal) => {
  const extraInfoData = [
    { label: 'Patient', value: `${patient?.first_name} ${patient?.last_name}` },
    { label: 'Drug', value: drug?.name },
  ];

  const id = useId();

  const title = appealRequestModalType === 'initiate_with_next_steps' ? 'Initiate appeal' : 'Revise appeal';

  let instructions = '';
  const isDupixentPilot = task?.pilot_drug_name === PharmaPilotDrug.DUPIXENT;

  switch (appealRequestModalType) {
    case 'initiate_with_next_steps':
      if (isDupixentPilot) {
        instructions = `Tandem will generate an appeal letter based on the patient's records and all available information regarding the denial.`;
      } else {
        instructions = `Tandem will generate an appeal letter based on the patient's records and all available information regarding
          the denial. Tandem will also start the enrollment process for Bridge or Patient Assistance (PAP), if available.`;
      }
      break;
    case 'revise_with_next_steps':
      if (isDupixentPilot) {
        instructions = `Tandem will generate a revised appeal letter.`;
      } else {
        instructions = `Tandem will generate a revised appeal letter and start the enrollment process for Bridge or Patient Assistance (PAP), if available.`;
      }
      break;
    case 'revise_only':
      // No top-level instructions needed
      break;
  }

  const isFirstDraft = appealRequestModalType === 'initiate_with_next_steps';
  const isInputRequired = !isFirstDraft; // If redraft request, we need context for the revision

  return (
    <Modal onClose={closeModal}>
      <ModalTitle>{title}</ModalTitle>

      <ModalBody>
        <ExtraInfoTable
          data={extraInfoData}
          minFieldLabelWidth="80px"
          marginTop="0"
          tableMarginBottom={MODAL_INNER_GAP}
          hairlineComponent={<Hairline $marginTop="0" $marginBottom="0" />}
        />
        {instructions && <Typography>{instructions}</Typography>}
        <Typography>
          {isFirstDraft && <strong>Optional: </strong>}Provide additional context, updated documentation, or specific
          edits to incorporate into the appeal {isFirstDraft ? 'letter' : 'revision'}.
        </Typography>
        <StyledCommentAndFileSection
          onSubmit={() => initiate.mutate()}
          isSubmitting={initiate.isPending}
          inputRef={inputRef}
          placeholder="Add additional information..."
          hideSubmitButton
          files={files}
          onFilesChange={onFilesChange}
          inputValue={inputValue}
          setInputValue={setInputValue}
        />
        <CheckboxContainer>
          <Checkbox
            checked={additionalInput.needs_draft_review}
            onChange={(e) => setAdditionalInput({ ...additionalInput, needs_draft_review: e.target.checked })}
            id={`draft-review-${id}`}
          />
          <CheckboxLabel renderedAs="label" htmlFor={`draft-review-${id}`} styledAs="bodySmallDMSans">
            I want to review the next draft of this appeal letter before submitting.
          </CheckboxLabel>
        </CheckboxContainer>
      </ModalBody>

      <ModalFooter>
        <Button variant="tertiary" onClick={closeModal}>
          Cancel
        </Button>
        <Button
          onClick={() => initiate.mutate()}
          isLoading={initiate.isPending}
          disabled={isInputRequired && !inputValue.trim()}
        >
          Submit
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AppealRequestModal;
